import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, Clock, Facebook, Instagram, Twitter, ChevronDown, Send, Sparkles, MapPin } from 'lucide-react';
import { Button } from './ui/Button';
import GlassButton from './GlassButton';

const GlassNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overscrollBehavior = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      isMegaMenu: true,
      dropdownCols: [
        [
          {
            title: 'Consultation',
            items: [
              { name: 'Wellness Exams', path: '/consultation' },
              { name: 'Preventative Care', path: '/preventive-care' },
              { name: 'Vaccinations', path: '/vaccination' },
              { name: 'Internal Medicine', path: '/internal-medicine' },
              { name: 'Dentistry', path: '/dentistry' }
            ]
          },
          {
            title: 'Specialty Care',
            items: [
              { name: 'Cardiology', path: '/cardiology' },
              { name: 'Oncology', path: '/oncology' },
              { name: 'Dermatology', path: '/dermatology' },
              { name: 'Ophthalmology', path: '/ophthalmology' },
              { name: 'Gynecology & Obstetrics', path: '/gynecology' },
              { name: 'Exotic Specialist', path: '/exotic-care' }
            ]
          }
        ],
        [
          {
            title: 'Emergency',
            items: [
              { name: '24/7 Emergency & Critical Care', path: '/critical-care' },
              { name: 'Pet Ambulance', path: '/critical-care' },
              { name: 'First Aid & Stabilization', path: '/critical-care' }
            ]
          },
          {
            title: 'Diagnostics & Lab',
            items: [
              { name: 'Digital X-rays', path: '/xray' },
              { name: 'CT scan', path: '/diagnostics' },
              { name: 'Ultrasound', path: '/diagnostics' },
              { name: '2D Echo', path: '/cardiology' },
              { name: 'PCR Testing', path: '/diagnostic-services' },
              { name: 'Electronic Medical Records', path: '/diagnostics' },
              { name: 'V Check Rapid', path: '/diagnostic-services' },
              { name: 'HT Vista Cancer Machine', path: '/diagnostics' }
            ]
          }
        ],
        [
          {
            title: 'Surgeries',
            items: [
              { name: 'General Surgery', path: '/surgeries' },
              { name: 'Spaying/Neutering', path: '/sterilization' },
              { name: 'Orthopaedic Surgery', path: '/orthopaedics' },
              { name: 'Tumor Removal', path: '/surgeries' },
              { name: 'Blood Transfusion', path: '/critical-care' }
            ]
          },
          {
            title: 'Rehab & Wellness',
            items: [
              { name: 'Physiotherapy', path: '/physiotherapy' },
              { name: 'Cold Laser Therapy', path: '/physiotherapy' },
              { name: 'Pet Grooming', path: '/grooming' },
              { name: 'Pet Boarding', path: '/boarding' },
              { name: 'Pharmacy', path: '/pharmacy' },
              { name: 'Pet Travel Docs', path: '/travel-docs' },
              { name: '50% NGO Discount', path: '/ngo-discount' }
            ]
          }
        ]
      ]
    },
    { name: 'Surgeries', path: '/surgeries' },
    { name: 'Branches', path: '/branches' },
    { name: 'Blog', path: '/blog' },
  ];

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300">
      {/* Top Bar - Optimized for Mobile */}
      <div className={`bg-[#010816] text-white text-[10px] px-3 sm:px-6 border-b border-white/5 transition-all duration-500 overflow-hidden flex items-center ${isScrolled || isMobileMenuOpen ? 'max-h-0 opacity-0 py-0' : 'max-h-[500px] opacity-100 py-3'}`}>
        <div className="max-w-[1440px] mx-auto w-full flex flex-col md:flex-row justify-between items-center gap-4 md:gap-2">
          <div className="flex flex-wrap gap-x-5 gap-y-3 items-center justify-center md:justify-start">
             <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 backdrop-blur-md">
                <Clock size={12} className="text-primary animate-pulse" />
                <span className="font-bold tracking-widest text-[11px] md:text-[12px]">24/7 Emergency</span>
             </div>
             <Link to="/branches" className="hidden md:flex items-center gap-2 hover:text-primary transition-colors text-center mt-1 md:mt-0">
                <MapPin size={12} className="text-primary opacity-90 hidden sm:block" />
                <span className="font-bold tracking-[0.1em] sm:tracking-widest opacity-80 text-[10px] sm:text-[10px] md:text-[11px] leading-relaxed">
                  Vanasthalipuram <span className="text-primary/60 px-0.5">|</span> Hasthinapuram <span className="text-primary/60 px-0.5">|</span> Turkayamjal
                </span>
             </Link>
             
             <div className="w-full md:hidden flex justify-center items-center gap-4 pt-2 border-t border-white/5">
                 <a href="tel:+919848954921" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone size={12} className="text-primary opacity-90" />
                    <span className="font-bold tracking-widest text-white text-[12px] md:text-[12px]">+91 9848954921</span>
                 </a>
                 <span className="w-px h-3 bg-white/20"></span>
                 <a href="mailto:dr.sureshhvet97@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail size={12} className="text-primary opacity-60" />
                    <span className="font-bold tracking-widest opacity-80 text-[10px] lowercase md:text-[11px]">dr.sureshhvet97@gmail.com</span>
                 </a>
             </div>
             
             <div className="hidden md:flex items-center gap-6">
                 <a href="tel:+919848954921" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone size={10} className="text-primary opacity-90" />
                    <span className="font-bold tracking-widest opacity-80 text-[9px] md:text-[11px]">+91 9848954921</span>
                 </a>
                 <a href="mailto:dr.sureshhvet97@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail size={10} className="text-primary opacity-60" />
                    <span className="font-bold tracking-widest opacity-80 text-[9px] md:text-[11px] lowercase">dr.sureshhvet97@gmail.com</span>
                 </a>
             </div>
          </div>
          <div className="hidden md:flex items-center gap-4">
             <span className="font-black uppercase tracking-[0.3em] text-[9px] opacity-40">Follow Sanctuary:</span>
             <div className="flex items-center gap-4">
               {[
                 { id: 'facebook', icon: <Facebook size={14}/>, url: '#' },
                 { id: 'instagram', icon: <Instagram size={14}/>, url: 'https://www.instagram.com/dr_sureshbabu' },
                 { id: 'twitter', icon: <Twitter size={14}/>, url: '#' }
               ].map((platform) => (
                 <a 
                   key={platform.id} 
                   href={platform.url}
                   target={platform.url !== '#' ? "_blank" : "_self"}
                   rel={platform.url !== '#' ? "noopener noreferrer" : ""}
                   className="hover:text-primary hover:-translate-y-0.5 transition-all duration-300 opacity-60 hover:opacity-100" 
                   aria-label={`Visit our ${platform.id} sanctuary page`}
                 >
                   {platform.icon}
                 </a>
               ))}
             </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`bg-white transition-all duration-500 w-full flex items-center ${isScrolled ? 'shadow-glass-heavy h-16 md:h-20 backdrop-blur-xl bg-white/95' : 'h-24 md:h-28 border-b border-border'}`}>
        <div className="max-w-[1440px] mx-auto px-6 w-full">
          <div className="flex items-center justify-between relative h-full">
            <Link to="/" className="relative flex items-center shrink-0 group z-50 h-full">
              <img 
                src="/images/logo-trasparent.png" 
                alt="Dr. Suresh Pet Care" 
                className="absolute top-1/2 -translate-y-1/2 h-14 sm:h-20 md:h-36 w-auto transition-transform duration-500 group-hover:scale-105 drop-shadow-sm"
              />
              {/* Spacer to maintain layout width for the absolute logo */}
              <div className="w-32 md:w-56 h-1"></div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <div key={link.name} className="relative group/nav">
                    <Link
                      to={link.path}
                      className={`text-[13px] font-bold font-display tracking-[0.05em] transition-all duration-500 flex items-center gap-2 py-4 relative ${
                        location.pathname === link.path ? 'text-primary' : 'text-navy/70 hover:text-primary hover:tracking-[0.1em]'
                      }`}
                    >
                      {link.name}
                      {(link.dropdown || link.isMegaMenu) && <ChevronDown size={14} className="transition-transform duration-500 group-hover/nav:rotate-180 opacity-60" />}
                      <span className={`absolute bottom-3 left-0 h-0.5 bg-primary transition-all duration-500 ${location.pathname === link.path ? 'w-full' : 'w-0 group-hover/nav:w-full'}`}></span>
                    </Link>

                    {/* Mega Menu */}
                    {link.isMegaMenu && (
                      <div className="absolute top-[100%] left-0 lg:-left-64 xl:-left-96 w-[1000px] bg-white shadow-glass-heavy border border-white/40 rounded-[3rem] opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all duration-700 flex p-12 gap-12 z-50 backdrop-blur-3xl bg-white/95 translate-y-4 group-hover/nav:translate-y-0">
                        {link.dropdownCols.map((col, idx) => (
                          <div key={idx} className="flex-1 flex flex-col gap-10">
                            {col.map((section) => (
                              <div key={section.title} className="flex flex-col gap-6">
                                <h4 className="font-display font-black text-navy border-b border-navy/5 pb-3 uppercase text-[10px] tracking-[0.3em] flex items-center gap-2">
                                  <Sparkles size={12} className="text-primary opacity-40" />
                                  {section.title}
                                </h4>
                                <ul className="flex flex-col gap-4">
                                  {section.items.map(item => (
                                    <li key={item.name}>
                                      <Link to={item.path} className="text-[13px] font-semibold text-navy/60 hover:text-primary transition-all duration-500 flex items-center group/item">
                                        <div className="w-0 h-0.5 bg-primary mr-0 group-hover/item:w-4 group-hover/item:mr-3 transition-all duration-500"></div>
                                        <span className="transform transition-transform duration-500 group-hover/item:translate-x-1 tracking-wide leading-none">
                                          {item.name}
                                        </span>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Link to="/#contact">
                <Button className="bg-[#25D366] hover:bg-[#1db958] text-white font-display font-black tracking-wide text-[12px] px-10 py-7 shadow-[0_20px_40px_rgba(37,211,102,0.2)] hover:shadow-[0_25px_50px_rgba(37,211,102,0.3)] group active:translate-y-1 transition-all duration-700 rounded-full flex items-center gap-4 relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-700"></div>
                  <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform relative z-10" />
                  <span className="relative z-10 font-bold">Book An Appointment</span>
                </Button>
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              className="lg:hidden p-3 text-navy bg-navy/5 rounded-2xl hover:bg-navy/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle Navigation"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMobileMenuOpen ? 'open' : 'closed'}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.3 }}
                >
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>
      </nav>
    </header>

    {/* Root Level Mobile Menu & Backdrop Blur */}
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-navy/40 backdrop-blur-md z-[2000]"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="lg:hidden fixed top-0 right-0 bottom-0 w-[85vw] sm:w-[75vw] bg-white z-[2010] shadow-2xl flex flex-col gap-0 overflow-y-auto overscroll-contain touch-pan-y [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            data-lenis-prevent
          >
            {/* Sticky Mobile Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-xl z-20 flex items-center justify-between p-8 border-b border-navy/5 shadow-sm">
              <span className="text-[10px] font-black tracking-[0.4em] text-primary uppercase italic">Sanctuary Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-3 bg-navy/5 rounded-2xl hover:bg-navy/10 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Navigation Area */}
            <div className="flex flex-col gap-6 p-8 pt-6">
                  {navLinks.map((link) => (
                    <div key={link.name} className="flex flex-col border-b border-navy/5 pb-6 last:border-0">
                      <div className="flex items-center justify-between group">
                        <Link
                          to={link.path}
                          className={`text-xl font-display font-extrabold tracking-tight transition-all duration-300 ${
                            location.pathname === link.path ? 'text-primary' : 'text-navy group-hover:text-primary'
                          }`}
                        >
                          {link.name}
                        </Link>
                        {(link.dropdown || link.isMegaMenu) && (
                          <button 
                            onClick={() => setActiveMobileDropdown(activeMobileDropdown === link.name ? null : link.name)}
                            className="p-3 bg-navy/5 rounded-xl text-navy transition-colors"
                          >
                            <ChevronDown size={18} className={`transition-transform duration-500 ${activeMobileDropdown === link.name ? 'rotate-180 text-primary' : ''}`} />
                          </button>
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {activeMobileDropdown === link.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: "circOut" }}
                            className="overflow-hidden"
                          >
                            <div className="flex flex-col gap-5 pt-4 pl-2">
                              {link.dropdownCols?.flat().map((section) => (
                                <div key={section.title} className="flex flex-col gap-3">
                                  <span className="font-display font-black text-primary uppercase text-[10px] tracking-[0.2em] flex items-center gap-2 opacity-100">
                                     <div className="w-2 h-[2px] rounded-full bg-primary"></div>
                                     {section.title}
                                  </span>
                                  <div className="flex flex-col gap-1 pl-4">
                                    {section.items.map(item => (
                                      <Link key={item.name} to={item.path} className="text-[14px] font-bold text-navy/80 hover:text-primary py-1.5 tracking-wide transition-colors block">
                                        {item.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-8 pb-12 space-y-10">
                  <div className="h-px bg-navy/5 w-full px-8" />
                  
                  {/* Sanctuary Contact Hub */}
                  <div className="px-8 space-y-8">
                    <div className="p-8 bg-[#010816] rounded-[2.5rem] border border-white/5 shadow-2xl relative overflow-hidden space-y-6 group/hub">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-50 pointer-events-none" />
                      
                      <div className="flex items-center gap-3 relative z-10">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border border-primary/20">
                          <Clock size={18} className="text-primary animate-pulse" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Status</p>
                          <p className="text-white font-bold text-sm">24/7 Emergency Active</p>
                        </div>
                      </div>

                      <div className="space-y-4 relative z-10">
                        <div className="flex items-start gap-3">
                          <MapPin size={16} className="text-primary mt-1 shrink-0" />
                          <p className="text-[12px] font-bold text-white/80 leading-relaxed">
                            Vanasthalipuram | Hasthinapuram | Turkayamjal
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-3 pt-2">
                          <a href="tel:+919848954921" className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl border border-white/10 shadow-sm active:scale-95 transition-all group/call">
                            <Phone size={16} className="text-primary group-hover/call:scale-110 transition-transform" />
                            <span className="font-bold text-white text-sm">+91 9848954921</span>
                          </a>
                          <a href="mailto:dr.sureshhvet97@gmail.com" className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl border border-white/10 shadow-sm active:scale-95 transition-all overflow-hidden group/mail">
                            <Mail size={16} className="text-primary group-hover/mail:scale-110 transition-transform" />
                            <span className="font-bold text-white text-[11px] truncate">dr.sureshhvet97@gmail.com</span>
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-navy/40 text-center">Follow Sanctuary</p>
                      <div className="flex justify-center gap-6">
                        {[
                          { id: 'fb', Icon: Facebook, url: '#' },
                          { id: 'ig', Icon: Instagram, url: 'https://www.instagram.com/dr_sureshbabu' },
                          { id: 'tw', Icon: Twitter, url: '#' }
                        ].map((social) => (
                          <a key={social.id} href={social.url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-[#010816] hover:text-white transition-all duration-500 border border-transparent hover:border-white/10">
                             <social.Icon size={18} />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="px-8 mt-4">
                    <Link to="/#contact" onClick={() => setIsMobileMenuOpen(false)} className="block shadow-glass-heavy rounded-full overflow-hidden group">
                      <GlassButton className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-display font-black tracking-wide rounded-full py-7 text-[13px] border-none flex items-center justify-center gap-4 transition-all duration-700">
                        <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /> 
                        <span>Book Sanctuary Appointment</span>
                      </GlassButton>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
    </>
  );
};

export default GlassNavbar;
